import {useState} from 'react'
import {Link, useParams} from 'react-router-dom'
import {useFormik} from 'formik'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import useAuth from '../../../../hooks/useAuth'
import {AuthFullScreen} from './AuthFullScreen'

export function ResetPassword() {
  const intl = useIntl()
  const {token} = useParams()
  const {actionLoading, resetPassword} = useAuth()
  const [showPassword, setShowPassword] = useState(false)
  const [showRepeat, setShowRepeat] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const schema = Yup.object().shape({
    password: Yup.string()
      .min(3, intl.formatMessage({id: 'FORM.ERROR.SYMBOL.MIN'}, {min: 3}))
      .max(50, intl.formatMessage({id: 'FORM.ERROR.SYMBOL.MAX'}, {max: 50}))
      .required(intl.formatMessage({id: 'FORM.ERROR.REQUIRED'})),
    repeatPassword: Yup.string()
      .oneOf([Yup.ref('password')], intl.formatMessage({id: 'FORM.ERROR.PASSWORD_DOES_NOT_MATCH'}))
      .required(intl.formatMessage({id: 'FORM.ERROR.REQUIRED'})),
  })

  const formik = useFormik({
    initialValues: {password: '', repeatPassword: ''},
    validationSchema: schema,
    onSubmit: async (values) => {
      if (!token) return
      setError(null)
      try {
        const action: any = await resetPassword({password: values.password, token})
        const payload = action?.payload
        if (payload?.success) {
          setSuccess(true)
        } else {
          setError(intl.formatMessage({id: 'ERROR.PASSWORD_RESET'}))
        }
      } catch {
        setError(intl.formatMessage({id: 'ERROR.PASSWORD_RESET'}))
      }
    },
  })

  const submitting = actionLoading || formik.isSubmitting

  return (
    <AuthFullScreen>
      <div className='marefati-login'>
          <div className='marefati-login__header'>
            <div className='marefati-login__secure'>
              <span className='marefati-login__lock'>
                <i className='bi bi-shield-lock-fill' />
              </span>
              <span className='marefati-login__secure-text'>Espace sécurisé Maarefati</span>
            </div>
          </div>

          <div className='marefati-login__title'>
            <h1>{intl.formatMessage({id: 'MENU.RESET_PASSWORD'})}</h1>
            <p>Suivre, Comprendre, Réussir</p>
          </div>

          {success ? (
            <div className='marefati-login__form'>
              <div className='marefati-login__success'>
                <i className='bi bi-check-circle-fill' />
                <p>{intl.formatMessage({id: 'SUCCESS.PASSWORD_RESET'})}</p>
              </div>
              <Link to='/auth/login' className='marefati-login__submit marefati-login__submit--link'>
                {intl.formatMessage({id: 'MENU.LOGIN'})}
              </Link>
            </div>
          ) : (
            <form className='marefati-login__form' onSubmit={formik.handleSubmit} noValidate>
              <div className='marefati-login__field'>
                <label className='marefati-login__label'>
                  {intl.formatMessage({id: 'INPUT.PASSWORD.LABEL'})}
                </label>
                <div className='marefati-login__password-wrapper'>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...formik.getFieldProps('password')}
                    className={
                      'marefati-login__input' +
                      (formik.touched.password && formik.errors.password
                        ? ' marefati-login__input--error'
                        : '')
                    }
                    placeholder='********'
                    autoComplete='new-password'
                  />
                  <button
                    type='button'
                    className='marefati-login__password-toggle'
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    <i className={showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'} />
                  </button>
                </div>
                {formik.touched.password && formik.errors.password && (
                  <div className='marefati-login__error'>{formik.errors.password}</div>
                )}
              </div>

              <div className='marefati-login__field'>
                <label className='marefati-login__label'>
                  {intl.formatMessage({id: 'INPUT.REPEAT_PASSWORD.LABEL'})}
                </label>
                <div className='marefati-login__password-wrapper'>
                  <input
                    type={showRepeat ? 'text' : 'password'}
                    {...formik.getFieldProps('repeatPassword')}
                    className={
                      'marefati-login__input' +
                      (formik.touched.repeatPassword && formik.errors.repeatPassword
                        ? ' marefati-login__input--error'
                        : '')
                    }
                    placeholder={intl.formatMessage({id: 'INPUT.REPEAT_PASSWORD.PLACEHOLDER'})}
                    autoComplete='new-password'
                  />
                  <button
                    type='button'
                    className='marefati-login__password-toggle'
                    onClick={() => setShowRepeat((v) => !v)}
                  >
                    <i className={showRepeat ? 'bi bi-eye-slash' : 'bi bi-eye'} />
                  </button>
                </div>
                {formik.touched.repeatPassword && formik.errors.repeatPassword && (
                  <div className='marefati-login__error'>{formik.errors.repeatPassword}</div>
                )}
              </div>

              {error && <div className='marefati-login__status'>{error}</div>}

              <div className='marefati-login__actions'>
                <Link to='/auth/login' className='marefati-login__secondary-btn'>
                  {intl.formatMessage({id: 'MENU.CANCEL'})}
                </Link>
                <button
                  type='submit'
                  disabled={submitting || !formik.isValid}
                  className='marefati-login__submit'
                >
                  {submitting && <span className='spinner-border spinner-border-sm me-2' />}
                  {intl.formatMessage({id: 'MENU.SUBMIT'})}
                </button>
              </div>
            </form>
          )}

          <div className='marefati-login__meta'>
            <span className='marefati-login__version'>v1.0.0</span>
            <span className='marefati-login__copy'>
              © {new Date().getFullYear()} Maarefati
            </span>
          </div>
        </div>
    </AuthFullScreen>
  )
}
